import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { SearchUser } from '../common/user-model';
import { RootState } from './store';
import { 
  deleteStudentService, 
  getAllStudentsMySchoolService, 
  getSchoolDetailsService, 
  registerStudentService, 
  schoolListService, 
  schoolRegisterUsersService, 
  searchUsersService, 
  StudentsSearchParams 
} from '../services/schoolsAxiosServices';
import { SchoolsType } from '../common/schools-types';


interface SchoolType {
  schoolStudents: SearchUser [];
  counts: number | null;
  pages: number;
	loadingSchoolStudents: boolean;
	errorSchoolStudents: string | undefined;
	loadingDeleteStudent: boolean;
	errorDeleteStudent: string | undefined;
  searchUsersLoading: boolean;
  searchUsersError: string | undefined;
  searchUsersList: SearchUser[]
  loadingCreateStudent: boolean;
  errorCreateStudent: string | undefined;
  loadingBulkCreateStudent: boolean;
  errorBulkCreateStudent: string | undefined;
  schoolsList: SchoolsType[]
  loadingGetSchools: boolean;
  errorGetSchools: string | undefined;
  schoolPages: number;
  schoolsCounts: number | null;
  schoolDetails: SchoolsType;
  loadingSchoolDetails: boolean;
  errorSchoolDetails: string | undefined;
}

export const initialState: SchoolType = {
  schoolStudents: [],
  counts: null,
  pages: 0,
  loadingSchoolStudents: false,
	errorSchoolStudents: undefined,
	loadingDeleteStudent: false,
	errorDeleteStudent: undefined,
  searchUsersLoading: false,
  searchUsersError: undefined,
  searchUsersList: [],
  loadingCreateStudent: false,
  errorCreateStudent: undefined,
  loadingBulkCreateStudent: false,
  errorBulkCreateStudent: undefined,
  schoolsList: [],
  loadingGetSchools: false,
  errorGetSchools: undefined,
  schoolPages: 0,
  schoolsCounts: null,
  schoolDetails: {
    name: "",
    id: "",
    town: "",
    students: 0,
    teachers: 0,
    availableStudents: 0,
  },
  loadingSchoolDetails: false,
  errorSchoolDetails: undefined,
}


export const getSchoolStudents = createAsyncThunk('school/getSchoolStudents', async ({currentPage, search}: StudentsSearchParams) => {
  let response;
  try {
    response = await getAllStudentsMySchoolService({currentPage, search})
  } catch (error) {
    console.error(error)
    throw error;
  }
  return response?.data
})

export const getSchoolDetails = createAsyncThunk('school/getSchoolDetails', async () => {
  let response;
  try {
    response = await getSchoolDetailsService()
  } catch (error) {
    console.error(error)
    throw error;
  }
  return response?.data
})


export const deleteSchoolStudent = createAsyncThunk('school/deleteSchoolStudent', async (userId: string) => {
  try {
    await deleteStudentService(userId)
  } catch (error) {
    console.error(error)
    throw error
  }
})

export const bulkCreateStudentCSV = createAsyncThunk('school/bulkCreateStudentCSV', async (file: File) => {
  try {
    await schoolRegisterUsersService(file)
  } catch (error) {
    console.error(error)
    throw error
  }
})

export const createSchoolStudent = createAsyncThunk('school/createSchoolStudent', async ({firstName, lastName, email}: SearchUser) => {
  try {
    await registerStudentService({firstName, lastName, email})
  } catch (error: any) {
    console.error(error)
    if (error.response && error.response.data && error.response.data.error) {
      console.error(error.response.data.error)
      throw error.response.data.error
    } else {
      console.error('An unknown error occurred')
      throw error.response.data.error
    }
    
  }
})

interface UserSearchParams {
	search: string;
	schoolId: string;
}

export const searchForStudents = createAsyncThunk('school/searchForStudents', async ({search, schoolId}: UserSearchParams) => {
  let response;
  try {
    response = await searchUsersService({search, schoolId})
  } catch (error) {
    console.error(error)
    throw error
  }
  return response?.data
})

export const getSchoolList = createAsyncThunk('school/getSchoolList', async (currentPage: number) => {
  let response;
  try {
    response = await schoolListService(currentPage)
  } catch (error) {
    console.error(error)
    throw error
  }
  return response?.data
})

export const schoolSlice = createSlice({
  name: 'school',
  initialState,
  reducers: {
    cleanCreateUserError: (state: SchoolType) => {
      state.errorCreateStudent = undefined
    },
    cleanBulkCreateUserError: (state: SchoolType) => {
      state.errorBulkCreateStudent = undefined
    },
    cleanSearchResult: (state: SchoolType) => {
      state.searchUsersList = []
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSchoolStudents.pending, (state: SchoolType) => {
        state.errorSchoolStudents = undefined
        state.loadingSchoolStudents = true
      })
      .addCase(getSchoolStudents.fulfilled, (state: SchoolType, action) => {
        state.loadingSchoolStudents = false
        state.counts = action.payload.count
        const pages = Math.ceil(action.payload.count / 20)
        state.pages = action.payload.count / 20 < 0 ? 2 : pages
        const allUsers: SearchUser[] = []
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        action.payload.results.forEach((user: any) => {
          const userObject = {
            firstName: user.first_name,
            id: user.id,
            lastName: user.last_name,
            email: user.email,
						avatar: user.avatar_url,
						isAutoCreated: user.is_auto_created,
						isStudent: user.is_student,
						isTeacher: user.is_teacher,
						isOrganizationHost: user.is_organization_host,
						isOrganizationMember: user.is_organization_member,
						autoSchoolAdd: user.auto_school_add
          }
          allUsers.push(userObject)
        })
        state.schoolStudents = allUsers
      })
      .addCase(getSchoolStudents.rejected, (state: SchoolType, action) => {
        state.loadingSchoolStudents = false
        state.errorSchoolStudents = action.error.message
      })
    builder
      .addCase(getSchoolList.pending, (state: SchoolType) => {
        state.errorGetSchools = undefined
        state.loadingGetSchools = true
      })
      .addCase(getSchoolList.fulfilled, (state: SchoolType, action) => {
        state.loadingGetSchools = false
        state.counts = action.payload.count
        const pages = Math.ceil(action.payload.count / 20)
        state.pages = action.payload.count / 20 < 0 ? 2 : pages
        const allSchools: SchoolsType[] = []
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        action.payload.results.forEach((school: any) => {
          const userObject = {
            name: school.name,
            id: school.id,
            town: school.town,
            website: school.website,
						logo: school.logo_url,
            isPaid: school.is_paid,
            description: school.description,
          }
          allSchools.push(userObject)
        })
        state.schoolsList = allSchools
      })
      .addCase(getSchoolList.rejected, (state: SchoolType, action) => {
        state.loadingGetSchools = false
        state.errorGetSchools = action.error.message
      })
    builder
      .addCase(deleteSchoolStudent.pending, (state: SchoolType) => {
        state.errorDeleteStudent = undefined
        state.loadingDeleteStudent = true
      })
      .addCase(deleteSchoolStudent.fulfilled, (state: SchoolType) => {
        state.loadingDeleteStudent = false

      })
      .addCase(deleteSchoolStudent.rejected, (state: SchoolType, action) => {
        state.loadingDeleteStudent = false
        state.errorDeleteStudent = action.error.message
      })
    builder
      .addCase(createSchoolStudent.pending, (state: SchoolType) => {
        state.errorCreateStudent = undefined
        state.loadingCreateStudent = true
      })
      .addCase(createSchoolStudent.fulfilled, (state: SchoolType) => {
        state.loadingCreateStudent = false

      })
      .addCase(createSchoolStudent.rejected, (state: SchoolType, action) => {
        state.loadingCreateStudent = false
        state.errorCreateStudent = action.error.message
      })
    builder
      .addCase(bulkCreateStudentCSV.pending, (state: SchoolType) => {
        state.errorBulkCreateStudent = undefined
        state.loadingBulkCreateStudent = true
      })
      .addCase(bulkCreateStudentCSV.fulfilled, (state: SchoolType) => {
        state.loadingBulkCreateStudent = false

      })
      .addCase(bulkCreateStudentCSV.rejected, (state: SchoolType, action) => {
        state.loadingBulkCreateStudent = false
        state.errorBulkCreateStudent = action.error.message
      })
    
    builder
      .addCase(searchForStudents.pending, (state: SchoolType) => {
        state.searchUsersError = undefined
        state.searchUsersLoading = true
      })
      .addCase(searchForStudents.fulfilled, (state: SchoolType, actions) => {
        state.searchUsersLoading = false
        const allUsers: SearchUser [] = []
        actions.payload.forEach((user: any) => {
          const userObject = {
            id: user.id,
            firstName: user.first_name,
            lastName: user.last_name,
            email: user.email,
            avatar: user.avatar_url
          }
          allUsers.push(userObject)
        })
        state.searchUsersList = allUsers
      })
      .addCase(searchForStudents.rejected, (state: SchoolType, action) => {
        state.searchUsersLoading = false
        state.searchUsersError = action.error.message
      })
    builder
      .addCase(getSchoolDetails.pending, (state: SchoolType) => {
        state.errorSchoolDetails = undefined
        state.loadingSchoolDetails = true
      })
      .addCase(getSchoolDetails.fulfilled, (state: SchoolType, actions) => {
        state.loadingSchoolDetails = false
        state.schoolDetails.students = actions.payload.students
        state.schoolDetails.teachers = actions.payload.teachers
        state.schoolDetails.name = actions.payload.name
        state.schoolDetails.id = actions.payload.id
        state.schoolDetails.availableStudents = actions.payload.available_students
      })
      .addCase(getSchoolDetails.rejected, (state: SchoolType, action) => {
        state.loadingSchoolDetails = false
        state.errorSchoolDetails = action.error.message
      })
  }
})


export const actions = schoolSlice.actions;

export const schoolSelector = (state: RootState) => state[schoolSlice.name];